/**
 * @file TriggerButtonContainer.tsx
 * @description Container component for TriggerButton that handles the press event logic.
 * Combines the external trigger press with internal behavior.
 */
import React, { useCallback } from "react";

import TriggerButtonView from "./TriggerButtonView";

/**
 * Props for TriggerButtonContainer component
 */
export interface TriggerButtonContainerProps {
  restTriggerProps: Record<string, unknown>;
  triggerOnPress?: (event: unknown) => void;
  onTriggerPress: (
    event: unknown,
    triggerOnPress?: (event: unknown) => void
  ) => void;
  selectedComparisonPlayerLabel: string;
}

/**
 * Container component for TriggerButton that handles press event composition
 * @param props - Component props
 * @param props.restTriggerProps - Props to spread on the pressable
 * @param props.triggerOnPress - Optional external press handler from parent
 * @param props.onTriggerPress - Handler that combines external and internal press behavior
 * @param props.selectedComparisonPlayerLabel - Label to display in the button
 */
function TriggerButtonContainer({
  restTriggerProps,
  triggerOnPress,
  onTriggerPress,
  selectedComparisonPlayerLabel,
}: TriggerButtonContainerProps): React.ReactElement {
  const handlePress = useCallback(
    (event: unknown) => {
      onTriggerPress(event, triggerOnPress);
    },
    [onTriggerPress, triggerOnPress]
  );

  return (
    <TriggerButtonView
      restTriggerProps={restTriggerProps}
      onPress={handlePress}
      selectedComparisonPlayerLabel={selectedComparisonPlayerLabel}
    />
  );
}

export default TriggerButtonContainer;
